import React from "react";
import { Row, Col, Badge } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import moment from "moment-timezone";
import styled from "styled-components";
import numeral from "numeral";

import { useUser } from "../../../contexts/UserContext";
import ProfilePicture from "../../common/ProfilePicture";
import { ucwords } from "../../../lib/stringHelpers";

const WrapperStyled = styled.div`
  padding: 1rem 0;
  padding-bottom: 0;
  border-bottom: 1px solid var(--border-color);

  &:hover {
    background-color: var(--light-secondary-color);
    cursor: pointer;
  }
`;

const JobListItem = ({ jobRequest }) => {
  const { user } = useUser();
  const navigate = useNavigate();

  const renderHelperView = () => {
    let profile = jobRequest.customer.profile || null;

    if (!profile) {
      profile = jobRequest.customer.profiles.find(
        (p) => p._id === jobRequest.requestFor
      );
    }

    const payoutOn = jobRequest.event?.payoutOn;
    const availableOn = jobRequest.event?.paymentIntent?.latest_charge?.transfer?.destination_payment?.balance_transaction?.available_on;

    return (
      <>
        <Col>
          <ProfilePicture
            user={{
              ...jobRequest.customer,
              customer: { ...jobRequest.customer },
            }}
          />
        </Col>
        {(payoutOn || availableOn) && (
          <Col>
            <label className="text-muted d-block">{payoutOn ? "Paid" : "Est. Payout"}</label>
            {moment.utc(payoutOn || (availableOn * 1000)).format("MMM D, YYYY")}
          </Col>
        )}
      </>
    );
  };

  const renderCustomerView = () => {
    if (jobRequest.helper) {
      return (
        <Col>
          <label className="text-muted d-block">Accepted Helper</label>
          <ProfilePicture
            user={{
              ...jobRequest.helper,
            }}
          />
        </Col>
      );
    }

    return (
      <Col>
        <label className="text-muted">Requsted Helpers</label>
        <Row>
          {jobRequest.requestedHelpers.map((helper, helperIdx) => {
            return (
              <Col xs={3} key={helperIdx}>
                <ProfilePicture
                  user={{
                    ...helper,
                  }}
                />
              </Col>
            );
          })}
        </Row>
      </Col>
    );
  };

  const getStatusColor = () => {
    switch (jobRequest.jobStatus.toLowerCase()) {
      case "new help request":
        return "info";
      case "booking confirmed":
        return "success";
      case "booking canceled":
        return "warning";
      default:
        return "primary";
    }
  };
  const calculateTotal = (helperTotal) => {
    if (typeof helperTotal !== "number") {
      return null;
    }

    const helperFee = jobRequest.serviceFee?.helper || 0;
    const customerFee = jobRequest.serviceFee?.customer || 0;

    if (user.userType === "helper") {
      return helperTotal - helperTotal * helperFee;
    }

    return helperTotal + helperTotal * customerFee;
  };

  const getPaymentDetails = () => {
    if (!Array.isArray(jobRequest.requestedHelperRates)) {
      return null;
    }

    let amount = "-";

    if (user.userType === "helper") {
      const helper = jobRequest.requestedHelperRates.find(
        (h) => h && h.helperId === user._id
      );

      if (helper && typeof helper.helperTotal === "number") {
        const total = calculateTotal(helper.helperTotal);
        amount = total === null ? "-" : numeral(total).format("$0,0.00");
      }
    } else if (jobRequest.helper) {
      const helper = jobRequest.requestedHelperRates.find(
        (h) => h && h.helperId === jobRequest.helper._id
      );

      if (helper && typeof helper.helperTotal === "number") {
        const total = calculateTotal(helper.helperTotal);
        amount = total === null ? "-" : numeral(total).format("$0,0.00");
      }
    } else {
      let min = 0;
      let max = 0;

      jobRequest.requestedHelperRates.forEach((item) => {
        if (!item || typeof item.helperTotal !== "number") {
          return;
        }

        const totalWithFee = calculateTotal(item.helperTotal);

        if (totalWithFee === null) {
          return;
        }

        min = min === 0 || totalWithFee < min ? totalWithFee : min;
        max = max === 0 || totalWithFee > max ? totalWithFee : max;
      });

      if (min !== 0 || max !== 0) {
        amount =
          min !== max
            ? `${numeral(min).format("$0,0.00")} - ${numeral(max).format(
                "$0,0.00"
              )}`
            : numeral(max).format("$0,0.00");
      }
    }

    if (
      jobRequest.event &&
      (jobRequest.event.holdAmount || jobRequest.event.completedAmount)
    ) {
      if (user.userType === "helper") {
        amount = jobRequest.event.completedAmount
          ? numeral(jobRequest.event.completedAmount - jobRequest.event.completedFeeAmount).format("$0,0.00")
          : numeral(jobRequest.event.holdAmount - jobRequest.event.holdFeeAmount).format("$0,0.00");
      } else {
        amount = jobRequest.event.completedAmount
          ? numeral(jobRequest.event.completedAmount).format("$0,0.00")
          : numeral(jobRequest.event.holdAmount).format("$0,0.00");
      }
    }

    return amount;
  };

  const navigateTo = `/bookings/${jobRequest._id}${
    jobRequest.event ? `/${jobRequest.event._id}` : ""
  }`;
  const startDt = moment(
    jobRequest.event
      ? jobRequest.event.startDate
      : jobRequest.dates.startDateTime
  );
  const endDt = jobRequest.event
    ? moment(jobRequest.event.endDate)
    : moment(startDt).add(jobRequest.hours, "hours");

  return (
    <WrapperStyled className="mb-3" onClick={() => navigate(navigateTo)}>
      <Row>
        <Col xs={5}>
          <Badge bg={getStatusColor()}>{ucwords(jobRequest.jobStatus)}</Badge>
          <p>
            {startDt.format("MM/DD h:mm a")} - {endDt.format("h:mm a")}
          </p>
        </Col>
        <Col>
          <label className="text-muted">Amount</label>
          <br />
          <p>{getPaymentDetails()}</p>
          {jobRequest.jobStatus === 'Booking Confirmed' && moment(jobRequest.event?.endDate).isBefore(moment()) && (
            <p className="text-muted">Customer Payment Pending</p>
          )}
        </Col>
        <Col className="d-none">
          <label className="text-muted">Job Type</label>
          <br />
          <p>
            {jobRequest.jobType.join(", ")}
            {jobRequest.isOvernight ? " Overnight" : ""}
          </p>
        </Col>
        {user.userType === "helper" ? renderHelperView() : null}
        {user.userType === "customer" ? renderCustomerView() : null}
      </Row>
    </WrapperStyled>
  );
};

export default JobListItem;
